import { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Card, Badge, Text, Stack, Flex, ActionIcon } from '@mantine/core';
import { CCMSDataTable, Tooltip, TextInput, StreamProgress } from '@/components';
import type { TableColumn } from '@/components/ccms-components/data-display/types';
import {
  useHttpClient,
  useBatchStreaming,
  AccounsellingStreamingService,
  LeaderCaseView,
} from '@actschurch/ccms-sdk';
import { IconEye, IconRefresh, IconSearch } from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import { brandBadgeStyle } from '@/utils/color';
import { badgeStyles, noWrap } from '@/utils/tableStyles';

function formatSDKDate(value: string | number | null | undefined): string {
  const raw = String(value ?? '');
  if (raw.length < 8) return '—';
  return `${raw.slice(0, 4)}-${raw.slice(4, 6)}-${raw.slice(6, 8)}`;
}

interface CasesTabProps {
  leaderId: string;
  active: boolean;
}

/**
 * Cases assigned to this leader.
 *
 * Reads `GET /api/leaders/{userId}/cases` through the SDK's
 * `AccounsellingStreamingService.streamCasesByLeader`, which is cache-composed server-side and
 * yields the (usually small) set in batches. This replaces the old approach of pulling the entire
 * `careCaseService.listView({})` result and filtering it client-side by `userId` — that only ever
 * matched the case's primary leader and pulled far more rows than needed.
 */
export default function CasesTab({ leaderId, active }: CasesTabProps) {
  const http = useHttpClient();
  const navigate = useNavigate();

  const [searchTerm, setSearchTerm] = useState('');

  const makeStream = useCallback(
    () => new AccounsellingStreamingService(http).streamCasesByLeader(leaderId, 50),
    [http, leaderId],
  );

  const {
    items: cases,
    isLoading: loading,
    progress,
    loadedCount,
    load,
  } = useBatchStreaming<LeaderCaseView>(makeStream, [http, leaderId]);

  useEffect(() => {
    if (active && leaderId) load();
  }, [active, leaderId, load]);

  // ── Search filtering — matches ID, name, need, status, counselees ─────────
  const filteredCases = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return cases;

    return cases.filter((row) => {
      const matchesText = [row.name, row.needName, row.statusName, row.counseleeNames]
        .some((field) => (field ?? '').toLowerCase().includes(term));
      return matchesText || String(row.caseID ?? '').includes(term);
    });
  }, [cases, searchTerm]);

  const columns: TableColumn<LeaderCaseView>[] = [
    {
      // Case name is the human-facing label; the numeric ID rides along underneath
      // rather than eating a column of its own.
      key: 'caseName',
      header: 'Case',
      accessor: (row) => row.name ?? '—',
      sortable: false,
      width: 240,
      render: (_, row) => (
        <Stack gap={0}>
          <Text size="sm" fw={600}>{row.name || `Case ${row.caseID}`}</Text>
          <Text size="xs" c="dimmed">#{row.caseID}</Text>
        </Stack>
      ),
    },
    {
      key: 'need',
      header: 'Need',
      accessor: (row) => row.needName ?? '—',
      sortable: false,
      width: 220,
      render: (value, row) =>
        row.needName ? (
          <Badge variant="filled" styles={badgeStyles} style={brandBadgeStyle(row.needColor)}>
            {value}
          </Badge>
        ) : (
          <Text size="sm" c="dimmed">—</Text>
        ),
    },
    {
      key: 'counselees',
      header: 'Counselees',
      accessor: (row) => row.counseleeNames ?? '—',
      sortable: false,
      width: 220,
    },
    {
      key: 'caseDateTime',
      header: 'Date Opened',
      accessor: (row) => String(row.caseDateTime ?? ''),
      sortable: false,
      width: 150,
      render: (value) => (
        <Text size="sm" style={noWrap}>{formatSDKDate(value as string)}</Text>
      ),
    },
    {
      key: 'statusName',
      header: 'Status',
      accessor: (row) => row.statusName ?? '—',
      sortable: false,
      align: 'center',
      width: 150,
      render: (value, row) => (
        <Badge variant="filled" styles={badgeStyles} style={brandBadgeStyle(row.statusColor)}>
          {value}
        </Badge>
      ),
    },
    {
      key: 'actions',
      header: '',
      accessor: (row) => row,
      sortable: false,
      align: 'center',
      width: 70,
      render: (_, row) => (
        <Tooltip label="View">
          <ActionIcon
            variant="light"
            color="blue"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/Counselling/Cases/${row.caseID}`);
            }}
          >
            <IconEye size={14} />
          </ActionIcon>
        </Tooltip>
      ),
    },
  ];

  return (
    <Card withBorder radius="md" padding="lg">
      <Flex justify="space-between" align="center" mb="md">
        <TextInput
          placeholder="Search by ID, name, need, status, or counselee"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.currentTarget.value)}
          leftSection={<IconSearch size={16} />}
          radius="md"
          styles={{ input: { width: '380px' } }}
          size="sm"
        />
        <Tooltip label="Refresh">
          <ActionIcon variant="subtle" onClick={() => load()} loading={loading} size="lg">
            <IconRefresh size={16} />
          </ActionIcon>
        </Tooltip>
      </Flex>

      <StreamProgress
        loading={loading}
        progress={progress}
        loadedCount={loadedCount}
        label="cases"
      />

      {filteredCases.length === 0 && !loading ? (
        <Text size="sm" c="dimmed" ta="center">
          {cases.length === 0 ? 'No cases found for this leader.' : 'No cases match your search.'}
        </Text>
      ) : (
        /* Let the table scroll inside the card rather than compressing columns until badges
           and dates ellipsise. Below 48em `ui-responsive-table` turns rows into cards. */
        <Box style={{ overflowX: 'auto' }}>
          <CCMSDataTable
            data={filteredCases}
            columns={columns}
            loading={loading && cases.length === 0}
            onRowClick={(row) => navigate(`/Counselling/Cases/${row.caseID}`)}
            striped
            highlightOnHover
          />
        </Box>
      )}
    </Card>
  );
}
